import React, { useState } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import moment from "moment";
import {
  border, textPrimary, textSecondary, textMuted, accent, font, easing, subjectGradientCss,
  subjectArtKey,
  success, warning, error as errorColor, infoBlue, avatarColorFor,
} from "../../layouts/schoolPortal/schoolPortalTheme";
import SubjectSvgArt from "../../layouts/schoolPortal/SubjectSvgArt";

/**
 * The pipeline. Four columns that ARE the product's loop: a student studies,
 * gets weak, is offered help, books it — and after the session, starts again
 * further along.
 *
 * CARDS MOVE THEMSELVES.
 * Every stage is derived server-side from facts — a risk signal, an outstanding
 * offer, a confirmed booking — so a student crosses the board when something
 * real happens to them, with no one dragging anything.
 *
 * DRAGGING PERFORMS THE ACT, IT DOES NOT SET A FIELD.
 * Because the stage is derived, there is nothing called "stage" to write. So
 * dropping a card into Struggling flags the student — a real SessionTrigger
 * with a named human on it — and dropping one back into Studying withdraws that
 * flag. Automated risk signals are evidence, not opinion, and are never cleared
 * by a drag.
 *
 * The two right-hand columns are not drop targets, and say so when you try:
 * Offered needs an actual offer with a time and a price, and Booked needs the
 * student to have said yes. Neither is something a school can assert for them.
 */

const COLUMNS = [
  { key: "studying",   title: "Studying",   hint: "Working through the syllabus",       color: textMuted,  droppable: true },
  { key: "struggling", title: "Struggling", hint: "Signals firing, or staff-flagged",   color: warning,    droppable: true },
  { key: "offered",    title: "Offered",    hint: "Help put in front of them",          color: infoBlue,   droppable: false,
    why: "Offering help needs a time and a price. Open the student and use Propose session." },
  { key: "booked",     title: "Booked",     hint: "Session confirmed ahead",            color: success,    droppable: false,
    why: "A student gets here by accepting an offer. You cannot book on their behalf from a board." },
];

const DORMANT_DAYS = 30;
// Never signed in is NOT the same as dormant, and the difference is the whole
// action: one needs their invite chasing, the other needs winning back. Folding
// them together labelled a student who was never seen as "not seen in 30 days",
// which is true and useless.
const neverSeen = (s) => !s.lastActive;
const isDormant = (s) =>
  Boolean(s.lastActive) && Date.now() - new Date(s.lastActive).getTime() > DORMANT_DAYS * 86400000;
const notStarted = (s) => Boolean(s.pipeline && s.pipeline.lessonsComplete === 0);

/**
 * Everyone stays in the column that is true of them; these are markers ON the
 * list, not buckets people fall out of.
 *
 * Ordered by distance from doing the work: getting on with it, started but
 * nothing finished, lapsed, never turned up at all. Each carries a different
 * action — nothing, a nudge, winning back, chasing the invite — which is why
 * they are four labels and not one.
 */
const SECTIONS = [
  { key: "active", label: null,                  test: (s) => !neverSeen(s) && !isDormant(s) && !notStarted(s) },
  { key: "new",    label: "Not started",         test: (s) => !neverSeen(s) && !isDormant(s) && notStarted(s) },
  { key: "gone",   label: "Not seen in 30 days", test: (s) => !neverSeen(s) && isDormant(s) },
  { key: "never",  label: "Never signed in",     test: neverSeen },
];

/**
 * Sorted on WHEN THE THING WAS MADE, not when it is scheduled — a booking taken
 * this morning for next month is recent news; one taken in June for tomorrow is
 * not. Direction comes from the server (`stageSort`) so the board and the list
 * cannot disagree.
 *
 * Struggling is the one column that runs oldest-first: the person who has been
 * struggling longest is the one nobody has got to, and newest-first buries them
 * under whoever just tripped a signal.
 */
function byRecency(rows, direction = "newest") {
  return [...rows].sort((a, b) => {
    const ta = a.stageAt ? new Date(a.stageAt).getTime() : 0;
    const tb = b.stageAt ? new Date(b.stageAt).getTime() : 0;
    if (!ta) return 1;
    if (!tb) return -1;
    return direction === "oldest" ? ta - tb : tb - ta;
  });
}

function split(rows, direction = "newest") {
  const out = [];
  for (const sec of SECTIONS) {
    const inSection = byRecency(rows.filter(sec.test), direction);
    inSection.forEach((row, i) => out.push({ row, divider: i === 0 ? sec.label : null }));
  }
  return out;
}

/**
 * The Offered column holds two opposite things, and conflating them hides the
 * urgent one.
 *
 *   Requested — the STUDENT asked. It is waiting on the school, and it is the
 *               only state on this board with a clock the school did not set:
 *               an unanswered request expires by itself and reads to the
 *               student as being ignored.
 *   Offered   — the school or the platform asked. It is waiting on the student,
 *               and there is nothing to do but wait.
 *
 * So they get their own headings at the weight of a column title, requested
 * first, rather than being one list you have to read to tell apart.
 */
const OFFER_GROUPS = [
  { key: "requested", title: "Requested", hint: "waiting on you", urgent: true },
  { key: "offered",   title: "Offered",   hint: "waiting on them", urgent: false },
];

/**
 * How much of the course, and how well — the bar's LENGTH and its COLOUR.
 *
 * This replaced "L6/16", which reads as a position in a queue. The syllabus is
 * advisory, not a gate: nothing stops a student doing airspace before weight
 * and balance and plenty do, so a lesson number describes an order they are not
 * following and goes backwards when they work ahead.
 */
/**
 * Colour is HEALTH, not score.
 *
 * You can be 95% done and in the red — nearly finished and falling apart on it
 * — and green having barely started, because so far you are fine. Length and
 * colour are answering different questions and are independent by design.
 *
 * Scoring alone was the wrong input: a student flagged "falling behind" on PACE
 * has perfectly good marks, so the bar read green while the card said they were
 * struggling.
 */
/**
 * Colour is health. When health is missing — an older API, a partial row — fall
 * back to the score rather than defaulting to green: an unknown state rendering
 * as "fine" is the failure mode where a broken screen looks like a healthy
 * school.
 */
const healthColour = (h, performance) => {
  if (h === "serious") return errorColor;
  if (h === "concern") return warning;
  if (h === "ok") return success;
  if (performance == null) return textMuted;
  return performance >= 85 ? success : performance >= 70 ? warning : errorColor;
};

function ProgressBar({ progress, health }) {
  if (!progress) return null;
  const { completion, performance } = progress;
  const c = healthColour(health, performance);
  return (
    <Tooltip
      arrow
      title={
        `${completion}% of the course covered` +
        (performance != null ? ` · averaging ${performance}% on what they have attempted` : " — nothing scored yet") +
        (health === "ok" ? " · no concerns" : health === "concern" ? " · something needs watching" : " · needs attention now")
      }
    >
      <Box sx={{ mt: 0.6 }}>
        <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "#F0EDE9", overflow: "hidden" }}>
          <Box sx={{ width: `${Math.max(completion, completion > 0 ? 2 : 0)}%`, height: "100%", backgroundColor: c }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.3 }}>
          <Typography sx={{ fontFamily: font, fontSize: "10px", color: textMuted, fontVariantNumeric: "tabular-nums" }}>
            {completion}% done
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: c, fontVariantNumeric: "tabular-nums" }}>
            {performance == null ? "no score yet" : `${performance}% scoring`}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * The bottom line of a card, which is not the same fact in every column.
 *
 *   booked / requested — that session: what it is worth and who it is with.
 *   everything else    — what the student has been worth to the school so far.
 */
function Footline({ s }) {
  const b = s.request || s.nextSession;
  const showSession = Boolean(b) && (s.stage === "booked" || s.offerKind === "requested");

  if (showSession) {
    return (
      <Box sx={{ mt: 0.6, pt: 0.6, borderTop: `1px solid ${border}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {/* How a session was funded is an accounting detail, not something
              the person reading this card acts on. The amount is stated; where
              the money came from is not. */}
          <Box sx={{
            display: "inline-flex", alignItems: "center", flexShrink: 0,
            backgroundColor: success, borderRadius: "999px", px: 1, py: "3px",
          }}>
            <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 800, color: "#fff", fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
              {money(b.price, b.currency)}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: font, fontSize: "10px", color: textMuted, flex: 1 }}>
            {s.request ? "this request" : "this session"}
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: s.request ? accent : success }}>
            {moment(b.at).format("D MMM")}
          </Typography>
        </Box>
        {/* Who is teaching it, at a size you can actually read. This card is
            about a session between two named people and the instructor was the
            smallest, greyest thing on it. */}
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, mt: 0.3 }}>
          <Typography sx={{ fontFamily: font, fontSize: "11.5px", fontWeight: 700, color: b.instructor ? textPrimary : warning }}>
            {b.instructor || "No instructor assigned"}
          </Typography>
          {s.value ? (
            <Typography sx={{ fontFamily: font, fontSize: "10px", color: textMuted, fontVariantNumeric: "tabular-nums" }}>
              · {money(s.value.revenue, s.value.currency)} to date
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  }

  if (!s.value) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: 0.6, pt: 0.6, borderTop: `1px solid ${border}` }}>
      <Typography sx={{ fontFamily: font, fontSize: "12px", fontWeight: 800, color: s.value.failedPayment ? errorColor : textPrimary, fontVariantNumeric: "tabular-nums" }}>
        {money(s.value.revenue, s.value.currency)}
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "10px", color: textMuted, flex: 1 }}>
        total spend{s.value.failedPayment ? " · card declined" : ""}
      </Typography>
    </Box>
  );
}

const money = (n, c) =>
  new Intl.NumberFormat(c === "GBP" ? "en-GB" : "en-US",
    { style: "currency", currency: c || "USD", maximumFractionDigits: 0 }).format(n || 0);

/**
 * One card, and it is defined ONCE.
 *
 * It used to be declared inside the column map, which gives it a new component
 * identity on every render — so React unmounted and remounted every card in
 * every column each time any state changed, including the drag-over state that
 * fires continuously while a card is in flight. That is what made the board
 * feel faded and unresponsive: it was being rebuilt from scratch several times
 * a second.
 */
const Card = React.memo(function Card({ s, divider, dragging, busy, onDragStart, onDragEnd, onOpen }) {
  const mine = Boolean(s.nextSession?.mine || s.request?.mine);
  return (
      <React.Fragment>

                {/* A marker on the list, not a bucket people fall out of. A
                    student who has not signed in for a month is still studying
                    or still struggling — they are just not around. */}
                {divider && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pt: 0.75, pb: 0.25 }}>
                    <Box sx={{ height: 1, flex: 1, backgroundColor: border }} />
                    <Typography sx={{ fontFamily: font, fontSize: "9.5px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: textMuted }}>
                      {divider}
                    </Typography>
                    <Box sx={{ height: 1, flex: 1, backgroundColor: border }} />
                  </Box>
                )}
                <Box
                  draggable
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onClick={onOpen}
                  sx={{
                    border: `1px solid ${border}`, borderRadius: "8px", p: 1.1,
                    backgroundColor: "#fff",
                    // "This one is yours" — a real tint, not a hint. On a
                    // school-wide board a CFI has to be able to find their own
                    // sessions without filtering it down to them, and at 4%
                    // opacity it was invisible.
                    //
                    // Set BEFORE the struggle edge, because `borderColor` is a
                    // shorthand for all four sides and would otherwise wipe out
                    // the left edge that carries the kind of struggle.
                    ...(mine ? {
                      backgroundColor: "#FDF3F4",
                      borderColor: `${accent}66`,
                      boxShadow: `inset 0 0 0 1px ${accent}22`,
                    } : {}),
                    // The kind's colour on a 3px edge. Preattentive, so a
                    // column can be read as a shape — "four of these are
                    // money" — without reading a word.
                    ...(s.struggle ? {
                      borderLeft: `3px solid ${s.struggle.colour}`,
                      paddingLeft: "10px",
                    } : {}),
                    // Pointer, not grab. The card's primary action is opening
                    // the student; dragging is the secondary one, and a grab
                    // cursor on every card told you the opposite.
                    cursor: "pointer",
                    // Only the card actually in flight, or actually saving,
                    // dims. Everything else stays at full strength.
                    opacity: busy ? 0.55 : dragging ? 0.4 : 1,
                    transition: `box-shadow 0.12s ${easing}, border-color 0.12s ${easing}`,
                    "&:hover": { borderColor: textMuted, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
                    <Box sx={{
                      width: 24, height: 24, borderRadius: "7px", flexShrink: 0,
                      backgroundColor: avatarColorFor(s.name), color: "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: font, fontWeight: 700, fontSize: "10px",
                    }}>
                      {s.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                    </Box>
                    <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: textPrimary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.name}
                    </Typography>
                    {mine && (
                      <Tooltip title="This session is with you" arrow>
                        <Typography sx={{
                          fontFamily: font, fontSize: "8.5px", fontWeight: 800, letterSpacing: "0.06em",
                          color: "#fff", backgroundColor: accent, borderRadius: "3px",
                          px: 0.6, py: "1.5px", flexShrink: 0, lineHeight: 1.2,
                        }}>
                          YOURS
                        </Typography>
                      </Tooltip>
                    )}
                    {s.staffFlagged && (
                      <Tooltip title="Flagged by a member of staff" arrow>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: accent, flexShrink: 0 }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Why this card is in this column. A board where you cannot
                      see why something moved is a board nobody trusts.
                      Where we know the KIND of struggle, it leads — colour and
                      a word for what it is, then the fact underneath. */}
                  {s.struggle ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                        <Typography sx={{
                          fontFamily: font, fontSize: "9.5px", fontWeight: 800, letterSpacing: "0.06em",
                          textTransform: "uppercase", color: s.struggle.colour,
                        }}>
                          {s.struggle.label}
                        </Typography>
                        {s.struggle.others > 0 && (
                          <Tooltip arrow title={s.struggle.all.slice(1).map((k) => k.sentence).join("  •  ")}>
                            <Typography sx={{ fontFamily: font, fontSize: "9.5px", fontWeight: 700, color: textMuted }}>
                              +{s.struggle.others}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography sx={{
                        fontFamily: font, fontSize: "11.5px", color: textSecondary, lineHeight: 1.4, mt: 0.15,
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {s.struggle.sentence}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{
                      fontFamily: font, fontSize: "11.5px", color: textSecondary, mt: 0.5, lineHeight: 1.4,
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                    }}>
                      {s.stageReason}
                    </Typography>
                  )}

                  <ProgressBar progress={s.progress} health={s.health} />

                  {/* The money line says whatever matters in THIS column.
                      A student with a session in front of them is a decision
                      about that session — who it is with and what it is worth.
                      Everyone else is a lifetime total. Showing the same figure
                      in every column made the one you can act on invisible. */}
                  <Footline s={s} />
                </Box>

      </React.Fragment>
  );
});

/**
 * A group session sits at the foot of Booked because it IS booked revenue —
 * it just is not about one student, so it cannot be a card in a per-student
 * column. A student appears here as well as on their own card when they have
 * both, which is correct: two commitments, two different days.
 */
function GroupFolder({ g }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{
      // Radius, border and shadow are the Learning Hub subject card's, not a
      // generic panel's — a class about airspace should look like the airspace
      // card the student taps, because it is the same subject.
      border: `1px solid ${border}`, borderRadius: "14px", mb: 0.75,
      backgroundColor: "#FAF8F5", overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
    }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          p: 1.1, cursor: "pointer", position: "relative", overflow: "hidden",
          // The subject's own gradient and artwork, both resolved through the
          // same two tables the student's page uses. No fallback to the title:
          // a title is not a subject key, and colouring by one was hiding the
          // real fault — group sessions saved with "airspace" instead of
          // "airspace-and-nas", so every subject lookup missed in silence.
          background: subjectGradientCss(g.subject),
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          transition: `filter 0.12s ${easing}`,
          "&:hover": { filter: "brightness(1.12)" },
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
          <Box sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}>
            <SubjectSvgArt subjectKey={subjectArtKey(g.subject)} width={220} height={160} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, position: "relative", zIndex: 1 }}>
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: "rgba(255,255,255,0.65)", flexShrink: 0 }}>
            {open ? "▾" : "▸"}
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: "#fff", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {g.title}
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, color: "rgba(255,255,255,0.9)" }}>
            {moment(g.at).format("D MMM")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, mt: 0.25, pl: 1.6, position: "relative", zIndex: 1 }}>
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: "rgba(255,255,255,0.6)", flex: 1 }}>
            {g.going}/{g.capacity} booked
            {g.seatsLeft ? ` · ${g.seatsLeft} free` : " · full"}
            {g.instructor ? " · " : ""}
            {g.instructor && (
              <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>{g.instructor}</Box>
            )}
          </Typography>
          {/* Closed, a folder still says what it is worth. */}
          {/* What the class is worth, always. */}
          <Tooltip arrow title={`${g.going} seat${g.going === 1 ? "" : "s"} at ${money(g.pricePerHead, g.currency)}`}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", flexShrink: 0,
              backgroundColor: success, borderRadius: "999px", px: 0.9, py: "2px",
            }}>
              <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 800, color: "#fff", fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
                {money(g.valueTotal, g.currency)}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {open && (
        <Box sx={{ px: 1.1, pb: 1.1, pt: 0.25 }}>
          {g.attendees.map((a) => (
            <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.4, borderTop: `1px solid ${border}` }}>
              <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textPrimary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.name}
              </Typography>
              {/* How far through the course each of them is. A class of six
                  where four are at 20% is a different lesson from one where
                  four are at 80%. */}
              {a.completion != null && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <Box sx={{ width: 28, height: 3, borderRadius: 2, backgroundColor: "#E4DED6", overflow: "hidden" }}>
                    <Box sx={{ width: `${a.completion}%`, height: "100%", backgroundColor: healthColour(a.health) }} />
                  </Box>
                  <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, fontVariantNumeric: "tabular-nums", minWidth: 26, textAlign: "right" }}>
                    {a.completion}%
                  </Typography>
                </Box>
              )}
              <Typography sx={{
                fontFamily: font, fontSize: "10.5px", fontWeight: 700, flexShrink: 0, minWidth: 44, textAlign: "right",
                color: textPrimary, fontVariantNumeric: "tabular-nums",
              }}>
                {money(g.pricePerHead, g.currency)}
              </Typography>
            </Box>
          ))}
          {!g.attendees.length && (
            <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, pt: 0.5 }}>
              Nobody has joined yet.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * A group session sits at the foot of Booked because it IS booked work — it
 * just is not about one student, so it cannot be a card in a per-student
 * column. A student appears here as well as on their own card when they have
 * both, which is correct: two commitments, two different days.
 */
function GroupBlock({ sessions = [] }) {
  if (!sessions.length) return null;
  const total = sessions.reduce((n, g) => n + (g.valueTotal || 0), 0);
  return (
    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${border}` }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, mb: 0.75 }}>
        <Typography sx={{ fontFamily: font, fontSize: "12px", fontWeight: 800, color: textPrimary }}>Group</Typography>
        <Typography sx={{ fontFamily: font, fontSize: "12px", fontWeight: 800, color: textSecondary, fontVariantNumeric: "tabular-nums" }}>
          {sessions.length}
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, flex: 1 }}>scheduled ahead</Typography>
        {total > 0 && (
          <Tooltip arrow title={`${money(total, sessions[0].currency)} across these classes`}>
            <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 800, color: success, fontVariantNumeric: "tabular-nums" }}>
              {money(total, sessions[0].currency)}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {sessions.map((g) => <GroupFolder key={g.id} g={g} />)}
    </Box>
  );
}


export default function PipelineBoard({ students = [], stages = {}, stageSort = {}, stageValue = {}, groupSessions = [], onMove, onOpen, busyId }) {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);

  const drop = (col) => (e) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setDragId(null);
    if (!id) return;
    const s = students.find((x) => String(x.id) === String(id));
    if (!s || s.stage === col.key) return;
    onMove(s, col);
  };

  return (
    // The board fills the space it is given and never grows the page. Each
    // column scrolls its own cards, so the headings — and everything above the
    // board — stay put. `position: sticky` was not enough: the layout's <main>
    // is itself the scroll container, so the page moved underneath it.
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, minmax(0, 1fr))" },
      gap: 1.25, alignItems: "stretch",
      height: "100%", minHeight: 0,
    }}>
      {COLUMNS.map((col) => {
        const rows = students.filter((s) => s.stage === col.key);
        const isOver = over === col.key;
        const sortDir = stageSort[col.key] || "newest";

        return (
          <Box
            key={col.key}
            onDragOver={(e) => {
              if (!col.droppable) return;
              e.preventDefault();
              // Only when it actually changes. dragover fires many times a
              // second, and setting state on every one of them re-rendered the
              // whole board continuously while a card was in flight.
              setOver((o) => (o === col.key ? o : col.key));
            }}
            onDragLeave={() => setOver((o) => (o === col.key ? null : o))}
            onDrop={drop(col)}
            sx={{
              border: `1px solid ${isOver ? accent : border}`,
              backgroundColor: isOver ? `${accent}08` : "#fff",
              borderRadius: "10px", overflow: "hidden", minHeight: 120,
              display: "flex", flexDirection: "column", maxHeight: "100%",
              transition: `background 0.12s ${easing}, border-color 0.12s ${easing}`,
            }}
          >
            {/* The column heading is outside the scrolling area, so it cannot
                move at all rather than merely sticking. */}
            <Box sx={{
              px: 1.5, py: 1.1, borderBottom: `1px solid ${border}`, backgroundColor: "#FAF8F5",
              flexShrink: 0,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: col.color, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 800, color: textPrimary, flex: 1 }}>
                  {col.title}
                </Typography>
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 800, color: textSecondary, fontVariantNumeric: "tabular-nums" }}>
                  {stages[col.key] ?? rows.length}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, mt: 0.15 }}>
                <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, flex: 1, minWidth: 0 }}>
                  {col.hint}
                </Typography>
                {/* COMMITTED is the term for work contracted and scheduled but
                    not yet delivered — some businesses call it backlog. It is
                    deliberately not called revenue: it has not been earned, and
                    a school prices a contract off this screen. PIPELINE is the
                    same thing one step earlier, where nobody has said yes yet. */}
                {/* The value of everything in the diary — the number that
                    answers "what is coming". */}
                {col.key === "booked" && stageValue.scheduled > 0 && (
                  <Tooltip arrow title="Scheduled and not yet delivered. Some businesses call this backlog.">
                    <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 800, color: success, fontVariantNumeric: "tabular-nums" }}>
                      {money(stageValue.scheduled, stageValue.currency)} scheduled
                    </Typography>
                  </Tooltip>
                )}
                {col.key === "offered" && stageValue.pipelineTotal > 0 && (
                  <Tooltip arrow title="Pipeline — offered or requested, nobody has said yes yet. Not scheduled, and not revenue.">
                    <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 800, color: warning, fontVariantNumeric: "tabular-nums" }}>
                      {money(stageValue.pipelineTotal, stageValue.currency)} pipeline
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Box sx={{
              p: 1, display: "flex", flexDirection: "column", gap: 0.75,
              overflowY: "auto", flex: 1, minHeight: 0,
              // A column with two cards should not reserve a screen of empty
              // space; one with twenty should scroll inside its own box.
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#D8D2CA", borderRadius: 3 },
            }}>
              {col.key === "offered" && OFFER_GROUPS.map((g) => {
                const list = byRecency(rows.filter((r) => r.offerKind === g.key), "newest");
                if (!list.length) return null;
                return (
                  <Box key={g.key} sx={{ mb: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, mb: 0.6 }}>
                      <Typography sx={{
                        fontFamily: font, fontSize: "12px", fontWeight: 800,
                        color: g.urgent ? accent : textPrimary,
                      }}>
                        {g.title}
                      </Typography>
                      <Typography sx={{ fontFamily: font, fontSize: "12px", fontWeight: 800, color: g.urgent ? accent : textSecondary, fontVariantNumeric: "tabular-nums" }}>
                        {list.length}
                      </Typography>
                      <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted }}>
                        {g.hint}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {/* No section dividers inside these groups. Requested
                          and Offered already ARE the structure here, and a
                          "Not started" heading nested under "Requested" reads
                          as nonsense — of course they have not started, that
                          is often why they asked. */}
                      {byRecency(list, "newest").map((s) => (
                        <Card
                          key={s.id} s={s} divider={null}
                          dragging={dragId === s.id} busy={busyId === s.id}
                          onDragStart={(e) => { setDragId(s.id); e.dataTransfer.setData("text/plain", String(s.id)); e.dataTransfer.effectAllowed = "move"; }}
                          onDragEnd={() => { setDragId(null); setOver(null); }}
                          onOpen={() => onOpen(s)}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}

              {col.key === "booked" && <GroupBlock sessions={groupSessions} />}

              {col.key !== "offered" && split(rows, sortDir).map(({ row: s, divider }) => (
                <Card
                  key={s.id} s={s} divider={divider}
                  dragging={dragId === s.id} busy={busyId === s.id}
                  onDragStart={(e) => { setDragId(s.id); e.dataTransfer.setData("text/plain", String(s.id)); e.dataTransfer.effectAllowed = "move"; }}
                  onDragEnd={() => { setDragId(null); setOver(null); }}
                  onOpen={() => onOpen(s)}
                />
              ))}

              {!rows.length && (
                <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, px: 0.5, py: 1.5, lineHeight: 1.5 }}>
                  {col.key === "studying" ? "Nobody is quietly getting on with it."
                    : col.key === "struggling" ? "Nobody is flagged or falling behind."
                    : col.key === "offered" ? "No offers outstanding."
                    : "Nothing booked ahead."}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
